import * as React from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import capitalize from "lodash/capitalize";
import { numberAbbreviationFormatter } from "@platform/fe-utils";
import { Typography } from "@platform/design-system";

import { ChartData } from "../types";

export type ColorsMap = Record<string, string>;

export interface StackedBarCardProps {
  title: string;
  /** Each series will be one color in the stack */
  series: ChartData;
  /** Show % labels inside each stacked segment (like your mock) */
  showSegmentPercents?: boolean;
  colorsMap?: ColorsMap;
}

/**
 * A Material-UI Card containing a stacked bar chart using Recharts.
 * Designed to resemble the BBE "Total Bonus by Carrier / BOB / Premium by LOC" charts.
 */
export function StackedBarCard({
  title,
  series,
  colorsMap,
  showSegmentPercents = false,
}: StackedBarCardProps): React.JSX.Element {
  // Precompute totals per category so we can show % labels
  const totalsByCategory = React.useMemo(() => {
    const totals: Record<string, string> = {};
    const sum = Object.values(series)
      .filter((v): v is number => typeof v === "number")
      .reduce((acc, curr) => acc + curr, 0);

    Object.entries(series).forEach(([key, value]) => {
      if (typeof value === "number") {
        const pct = Math.round((value / sum) * 100);
        totals[key] = `${pct}%`;
      }
    });

    return totals;
  }, [series]);

  const metricKeys =
    Object.keys(series)?.length > 0
      ? Object.keys(series).filter((key) => key !== "name")
      : [];

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        padding: 2,
      }}
    >
      <CardHeader
        title={
          <Typography variant="header-small" textAlign="center">
            <strong>{title}</strong>
          </Typography>
        }
        sx={{
          p: 0,
        }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          pb: `8px !important`,
          height: "100%",
        }}
      >
        <ResponsiveContainer width="100%" aspect={1.5}>
          <BarChart responsive data={[series]} barCategoryGap="30%">
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => capitalize(value)}
              hide
            />
            <YAxis
              domain={[0, "auto"]}
              tick={{ fontSize: 12 }}
              tickFormatter={numberAbbreviationFormatter}
              allowDecimals={false}
            />

            {metricKeys.map((key) => (
              <Bar
                key={key}
                stackId="a"
                dataKey={key}
                background={false}
                fill={colorsMap?.[key]}
              >
                {showSegmentPercents && (
                  <LabelList
                    dataKey={key}
                    position="center"
                    formatter={(value) =>
                      value !== 0 ? totalsByCategory[key] : null
                    }
                    fill={"#FFF"}
                    style={{ fontSize: 12 }}
                  />
                )}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1,
            mt: 1,
          }}
        >
          {metricKeys.map((key) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  backgroundColor: colorsMap?.[key],
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption">{key}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
